// src/components/CalorieTargetsModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CalorieMacroCalculator from './CalorieMacroCalculator'; // Ajusta la ruta

const CalorieTargetsModal = ({
    show,
    onClose,
    onSave,
    currentProfileData, // { age, sex, height, weight, activityLevel, goal, targetCalories, proteinPercent, carbPercent, fatPercent }
}) => {
    const [editableTargets, setEditableTargets] = useState({
        calories: 2000,
        protein: 30,
        carb: 40,
        fat: 30,
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (show && currentProfileData) {
            setEditableTargets({
                calories: currentProfileData.target_calories || 2000,
                protein: currentProfileData.protein_percent_target || 30,
                carb: currentProfileData.carb_percent_target || 40,
                fat: currentProfileData.fat_percent_target || 30,
            });
            setError(''); // Limpiar errores al abrir
        }
    }, [show, currentProfileData]);

    const handleTargetsChange = (newTargets) => {
        // newTargets = { calories, protein, carb, fat }
        setEditableTargets(newTargets);
    };

    const handleSaveChanges = () => {
        setError('');
        const { calories, protein, carb, fat } = editableTargets;

        if (calories < 1000 || calories > 6000) {
            setError("Target Calories must be between 1000 and 6000.");
            return;
        }
        const totalPercent = Math.round(protein + carb + fat);
        if (totalPercent !== 100) {
            setError(`Macronutrient percentages must sum to 100%. Current sum: ${totalPercent}%.`);
            return;
        }
        if (protein <= 0 || carb <= 0 || fat <= 0 || protein > 100 || carb > 100 || fat > 100) {
            setError("Macronutrient percentages must be valid (1-100).");
            return;
        }

        onSave({
            target_calories: calories,
            protein_percent_target: protein,
            carb_percent_target: carb,
            fat_percent_target: fat,
            bmr: currentProfileData.bmr, // Asumiendo que tienes el BMR precalculado
            tdee: currentProfileData.tdee, // Asumiendo que tienes el TDEE precalculado

        });
        onClose();
    };

    // Profile data solo para las sugerencias visuales del CalorieMacroCalculator
    const profileForSuggestions = currentProfileData ? {
        age: currentProfileData.age,
        sex: currentProfileData.sex,
        height: currentProfileData.height,
        weight: currentProfileData.weight,
        activityLevel: currentProfileData.activity_level, // Asegúrate que el nombre coincida
        primary_goal: currentProfileData.primary_goal, // Asume que tienes esto, o adáptalo
        bmr: currentProfileData.bmr,                                 // El BMR precalculado
        tdee: currentProfileData.tdee,                                // El TDEE precalculado
    } : null;

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Adjust Daily Calorie & Macro Targets</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {currentProfileData && ( // Solo mostrar si tenemos datos del perfil
                    <CalorieMacroCalculator
                        profileData={profileForSuggestions}
                        currentCalories={editableTargets.calories}
                        currentProteinPercent={editableTargets.protein}
                        currentCarbPercent={editableTargets.carb}
                        currentFatPercent={editableTargets.fat}
                        onTargetsChange={handleTargetsChange}
                        areProfileInputsEditable={false} // El perfil no se edita aquí
                        areTargetsEditable={true}
                        showApplyButton={false} // Usaremos el botón del modal
                        cardHeaderTitle="Your Daily Targets"
                        showSuggestionsBasedOnProfile={true}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveChanges}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

CalorieTargetsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    currentProfileData: PropTypes.shape({
        age: PropTypes.any,
        sex: PropTypes.string,
        height: PropTypes.any,
        weight: PropTypes.any,
        activity_level: PropTypes.string,
        primary_goal: PropTypes.string, // Necesario para 'adjustCaloriesForGoal' si se usa internamente
        target_calories: PropTypes.number,
        protein_percent_target: PropTypes.number,
        carb_percent_target: PropTypes.number,
        fat_percent_target: PropTypes.number,
        bmr: PropTypes.number, // BMR precalculado
        tdee: PropTypes.number, // TDEE precalculado
    }),
};

export default CalorieTargetsModal;